import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpenIcon, HomeIcon, CalculatorIcon, InfoIcon, MenuIcon, XIcon, UserIcon, LogOutIcon, ChevronDownIcon, LayoutDashboardIcon, FileTextIcon, RocketIcon, HelpCircleIcon, TrendingUpIcon, Building2Icon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useProgress } from '../hooks/useProgress';
export function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    user,
    isAuthenticated,
    logout,
    isRentalCloser
  } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  // Define navigation items based on user type
  const getNavItems = () => {
    if (!isAuthenticated) {
      // Public navigation
      return [{
        path: '/cariera-3',
        label: 'Start Carieră',
        icon: RocketIcon
      }, {
        path: '/cursuri',
        label: 'Training',
        icon: BookOpenIcon
      }, {
        path: '/support',
        label: 'Suport',
        icon: HelpCircleIcon
      }, {
        path: '/despre',
        label: 'Despre',
        icon: InfoIcon
      }];
    }
    // Rental Closer navigation (rentalcloser@gmail.com)
    if (isRentalCloser) {
      return [{
        path: '/inchiriere',
        label: 'Închiriere',
        icon: HomeIcon
      }, {
        path: '/cursuri',
        label: 'Training',
        icon: BookOpenIcon
      }, {
        path: '/tranzactii',
        label: 'Tranzacții',
        icon: FileTextIcon
      }, {
        path: '/support',
        label: 'Suport',
        icon: HelpCircleIcon
      }];
    }
    // Trainee navigation (default for authenticated users)
    return [{
      path: '/dashboard',
      label: 'Dashboard',
      icon: LayoutDashboardIcon
    }, {
      path: '/cursuri',
      label: 'Training',
      icon: BookOpenIcon
    }, {
      path: '/tranzactii',
      label: 'Tranzacții',
      icon: FileTextIcon
    }, {
      path: '/support',
      label: 'Suport',
      icon: HelpCircleIcon
    }];
  };
  const navItems = getNavItems();
  const handleLogout = () => {
    logout();
    setProfileMenuOpen(false);
    navigate('/');
  };
  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setProfileMenuOpen(false);
      }
    };
    if (profileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [profileMenuOpen]);
  // Get user badge text
  const getUserBadge = () => {
    if (!isAuthenticated || !user) return null;
    if (isRentalCloser) return 'Rental Closer';
    return 'Trainee';
  };
  const userBadge = getUserBadge();
  return <>
      <nav className="bg-white shadow-sm sticky top-0 z-50" data-id="element-1968">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1969">
          <div className="flex justify-between items-center h-16" data-id="element-1970">
            <Link to={isAuthenticated ? isRentalCloser ? '/inchiriere' : '/dashboard' : '/'} className="flex items-center space-x-2" data-id="element-1971">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#132C53] rounded-lg flex items-center justify-center" data-id="element-1972">
                <Building2Icon className="w-4 h-4 sm:w-6 sm:h-6 text-white" data-id="element-1973" />
              </div>
              <span className="text-lg sm:text-xl font-bold text-[#132C53]" data-id="element-1974">
                pro.cautlocuință.ro
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-3" data-id="element-1975">
              <div className="bg-gray-100 rounded-full p-1 space-x-1 flex items-center" data-id="element-1976">
                {navItems.map(item => {
                const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
                const Icon = item.icon;
                return <Link key={item.path} to={item.path} className="relative px-4 py-2 rounded-full text-sm font-medium transition-colors z-10" data-id="element-1977">
                      {isActive && <motion.div layoutId="activeNav" className="absolute inset-0 bg-white shadow-md rounded-full" transition={{
                    type: 'spring',
                    stiffness: 380,
                    damping: 30
                  }} data-id="element-1978" />}
                      <span className={`relative flex items-center space-x-1.5 ${isActive ? 'text-[#357BEB]' : 'text-gray-700'}`} data-id="element-1979">
                        <Icon className="w-3 h-3" data-id="element-1980" />
                        <span data-id="element-1981">{item.label}</span>
                      </span>
                    </Link>;
              })}
              </div>

              {/* User Badge */}
              {userBadge && <div className={`px-4 py-2 rounded-full text-sm font-bold shadow-md ${isRentalCloser ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white' : 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white'}`} data-id="element-1982">
                  {userBadge}
                </div>}
            </div>

            {/* Mobile Menu Button */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-1983">
              {mobileMenuOpen ? <XIcon className="w-6 h-6 text-[#132C53]" data-id="element-1984" /> : <MenuIcon className="w-6 h-6 text-[#132C53]" data-id="element-1985" />}
            </button>

            {/* Desktop CTAs */}
            <div className="hidden md:flex items-center space-x-3" data-id="element-1986">
              {isAuthenticated && user ? <div className="relative" ref={profileMenuRef} data-id="element-1987">
                  <button onClick={() => setProfileMenuOpen(!profileMenuOpen)} className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-1988">
                    {user.photo ? <img src={user.photo} alt={user.fullName} className="w-8 h-8 rounded-full object-cover" data-id="element-1989" /> : <div className="w-8 h-8 bg-[#357BEB] rounded-full flex items-center justify-center" data-id="element-1990">
                        <UserIcon className="w-4 h-4 text-white" data-id="element-1991" />
                      </div>}
                    <span className="text-sm font-medium text-[#132C53]" data-id="element-1992">
                      {user.fullName || 'Profil'}
                    </span>
                    <ChevronDownIcon className="w-4 h-4 text-gray-500" data-id="element-1993" />
                  </button>

                  <AnimatePresence data-id="element-1994">
                    {profileMenuOpen && <motion.div initial={{
                  opacity: 0,
                  y: -10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -10
                }} transition={{
                  duration: 0.2
                }} className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-2" data-id="element-1995">
                        <div className="px-4 py-3 border-b border-gray-200" data-id="element-1996">
                          <p className="text-sm font-semibold text-[#132C53]" data-id="element-1997">
                            {user.fullName || 'Utilizator'}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-1998">{user.email}</p>
                          {userBadge && <span className={`inline-block mt-2 px-2 py-1 rounded-full text-xs font-bold ${isRentalCloser ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`} data-id="element-1999">
                              {userBadge}
                            </span>}
                        </div>
                        <Link to="/profile" onClick={() => setProfileMenuOpen(false)} className="flex items-center space-x-2 px-4 py-2 hover:bg-gray-50 transition-colors" data-id="element-2000">
                          <UserIcon className="w-4 h-4 text-gray-500" data-id="element-2001" />
                          <span className="text-sm text-gray-700" data-id="element-2002">
                            Profilul meu
                          </span>
                        </Link>
                        <div className="border-t border-gray-200 mt-2 pt-2" data-id="element-2003">
                          <button onClick={handleLogout} className="flex items-center space-x-2 px-4 py-2 w-full hover:bg-gray-50 transition-colors text-red-600" data-id="element-2004">
                            <LogOutIcon className="w-4 h-4" data-id="element-2005" />
                            <span className="text-sm" data-id="element-2006">Deconectare</span>
                          </button>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div> : <>
                  <Link to="/auth/login" className="flex items-center space-x-2 text-[#132C53] px-4 py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors text-sm border border-gray-200" data-id="element-2007">
                    <UserIcon className="w-4 h-4" data-id="element-2008" />
                    <span data-id="element-2009">Logare</span>
                  </Link>
                  <Link to="/auth/register" className="bg-[#357BEB] text-white px-6 py-2 rounded-lg font-medium hover:bg-[#2868d8] transition-colors text-sm" data-id="element-2010">
                    Devino Agent
                  </Link>
                </>}
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence data-id="element-2011">
          {mobileMenuOpen && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.2
        }} className="md:hidden border-t border-gray-200 bg-white" data-id="element-2012">
              <div className="px-4 py-4 space-y-2" data-id="element-2013">
                {/* User Badge in Mobile */}
                {userBadge && <div className={`px-4 py-2 rounded-lg text-center text-sm font-bold mb-2 ${isRentalCloser ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white' : 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white'}`} data-id="element-2014">
                    {userBadge}
                  </div>}

                {navItems.map(item => {
              const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
              const Icon = item.icon;
              return <Link key={item.path} to={item.path} onClick={() => setMobileMenuOpen(false)} className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${isActive ? 'bg-[#357BEB] text-white' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-2015">
                      <Icon className="w-5 h-5" data-id="element-2016" />
                      <span className="font-medium" data-id="element-2017">{item.label}</span>
                    </Link>;
            })}
                {isAuthenticated && user ? <>
                    <Link to="/profile" onClick={() => setMobileMenuOpen(false)} className="flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors border-t border-gray-200 mt-2 pt-4" data-id="element-2018">
                      <UserIcon className="w-5 h-5" data-id="element-2019" />
                      <span className="font-medium" data-id="element-2020">Profilul meu</span>
                    </Link>
                    <button onClick={() => {
                handleLogout();
                setMobileMenuOpen(false);
              }} className="flex items-center space-x-3 w-full text-red-600 px-4 py-3 rounded-lg font-medium hover:bg-red-50 transition-colors" data-id="element-2021">
                      <LogOutIcon className="w-5 h-5" data-id="element-2022" />
                      <span data-id="element-2023">Deconectare</span>
                    </button>
                  </> : <>
                    <Link to="/auth/login" onClick={() => setMobileMenuOpen(false)} className="flex items-center space-x-3 w-full text-[#132C53] px-4 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors border border-gray-200" data-id="element-2024">
                      <UserIcon className="w-5 h-5" data-id="element-2025" />
                      <span data-id="element-2026">Logare în cont</span>
                    </Link>
                    <Link to="/auth/register" onClick={() => setMobileMenuOpen(false)} className="block w-full bg-[#357BEB] text-white px-4 py-3 rounded-lg font-medium hover:bg-[#2868d8] transition-colors text-center" data-id="element-2027">
                      Devino Agent
                    </Link>
                  </>}
              </div>
            </motion.div>}
        </AnimatePresence>
      </nav>
    </>;
}